import { HttpClient, HttpContext } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { CommandService } from '@helios/commands'
import {
  HANDLE_ERROR_AND_THROW,
  HELIOS_COMMAND_HTTP_TOKEN,
} from '@helios/utils'
import { Observable } from 'rxjs'
import {
  PositionActionRequest,
  PositionActionResponse,
} from '../models/position-action.model'
import { FLEX_POSITIONS_URL } from './http-flex-position-comparison.service'
import { PositionsTaskService } from './positions-task.service'

/**
 * Same two load-bearing `flex-positions` segments as every other Flex Positions URL in this panel —
 * see {@link FLEX_POSITIONS_URL}'s own comment for why neither is a typo, and why the `helios-`
 * host prefix cannot be dropped. Under `<base href="/helios/">` this resolves to
 * `/helios/helios-flex-positions/flex-positions/actions`, which the committed
 * `helios-flex-positions` proxy rule (dev) and nginx location block (prod) already route onto the
 * flex-positions backend — no proxy change is needed, and none may be committed.
 *
 * The CIAM bearer token and the `helios-session-id` header are attached globally by
 * AuthTokenInterceptor and SessionInterceptor; never set either by hand here.
 */
export const POSITION_ACTIONS_URL = `${FLEX_POSITIONS_URL}/flex-positions/actions`

/** Panel name carried on the command headers, as it appears to a user reviewing their commands. */
const PANEL_NAME = 'Positions 2.0'

/**
 * The real dispatch: one POST per action. Bound by the panel whenever
 * {@link POSITION_ACTIONS_ENDPOINT_AVAILABLE} is true, which it now is.
 *
 * Deliberately transport-only. The request body is assembled by `utils/action-request.ts` and a
 * rejection is decoded by `utils/action-error.ts`, so neither the id bucketing nor the
 * `validationId` vocabulary is buried in an HTTP client — this class stays swappable against
 * {@link StubPositionsTaskService} with no behaviour hidden in it.
 */
@Injectable()
export class HttpPositionsTaskService extends PositionsTaskService {
  constructor(
    private readonly http: HttpClient,
    private readonly commandService: CommandService,
  ) {
    super()
  }

  /**
   * POST the action as a Helios COMMAND, not as a plain request. Three pieces beyond the body, all
   * load-bearing and all adopted from PVAL-26416's parallel implementation of this same POST:
   *
   *  - `generateTemporaryHeaders` stamps `helios-internal-cmd-panel` / `-action`, which is what
   *    names this action in the user's own commands drawer. Without them the command lands
   *    unattributed. Same call, same order, as `HttpExternalTransactionsService.callActionsApi`.
   *  - `HELIOS_COMMAND_HTTP_TOKEN` is what tags the call as a command so that drawer opens at all.
   *  - `HANDLE_ERROR_AND_THROW` is what lets a rejection reach this call's subscriber. This one is
   *    easy to omit and impossible to notice in a unit test: the global ErrorInterceptor otherwise
   *    reports the error and returns EMPTY, so `dispatch`'s error callback never runs, no toast is
   *    raised, `actionError` stays false — and the action silently appears to have succeeded while
   *    the grids sit unchanged. The `utils/action-error.ts` vocabulary is dead code without it.
   *
   * The CIAM bearer token and `helios-session-id` are still attached globally by AuthTokenInterceptor
   * and SessionInterceptor; never set either by hand here.
   */
  submit(request: PositionActionRequest): Observable<PositionActionResponse> {
    return this.http.post<PositionActionResponse>(
      POSITION_ACTIONS_URL,
      request,
      {
        headers: this.commandService.generateTemporaryHeaders(
          PANEL_NAME,
          request.actionType,
        ),
        context: new HttpContext()
          .set(HELIOS_COMMAND_HTTP_TOKEN, true)
          .set(HANDLE_ERROR_AND_THROW, true),
      },
    )
  }
}
